import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuthStepEffects } from "@/hooks/useAuthStepEffects";
import { useNavigate, useLocation } from "react-router-dom";
import { MButton } from "@/components/ui/atoms/MButtons";
import { isValidEmail, isValidMobile } from "@/utils";
import MInputField from "@/components/ui/atoms/MInputField";
import { useAuth } from "@/hooks/useAuth";
import { useDialCode } from "@/hooks/useDialCode";
import MDialCode from "@/components/ui/molecules/MDialCode";
import { dialCodeOptions } from "@/utils/countryCodes";
import MOnboardingHeader from "@/components/ui/organisms/MOnboardingHeader";
import { getPreferredLoginMethod } from '@/utils/storage-utils';
import { useKeyboardBottomPadding } from '@/hooks/useKeyboardBottomPadding';
import { useFixMobileKeyboard } from '@/hooks/useFixMobileKeyboard';

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [otpValue, setOtpValue] = useState("");
  const [step, setStep] = useState<"input" | "otp">("input");
  const [showDialCodeDrawer, setShowDialCodeDrawer] = useState(false);

  const preferredMethod = getPreferredLoginMethod();
  const loginMode = location.state?.mode || preferredMethod || "mobile";
  const isLoginFlow = location.state?.isLogin || true; // Default to true for login form

  // useAuth for login logic
  const {
    sendOtp,
    verifyOtp,
    loading,
    inputValue,
    setInputValue,
    setOtp,
    setOtpSent,
    setError,
    setVerifyType,
  } = useAuth();

  // Unified hook for auth effects (verifyType, resets, resend timer)
  // Adapter for setVerifyType to accept 'sms' | 'email' and convert to 'mobile' | 'email'
  const setVerifyTypeAdapter = (v: "sms" | "email") =>
    setVerifyType(v === "sms" ? "mobile" : "email");

  // useDialCode for country code
  const { dialCode, setDialCode } = useDialCode("+91");

  // Use unified hook for all OTP/resend/CTA logic
  const { resendTimer, handleResend, handleCta } = useAuthStepEffects({
    mode: loginMode,
    setVerifyType: setVerifyTypeAdapter,
    setStep,
    setOtpSent,
    setError,
    setOtpValue,
    setInputValue,
    setOtp,
    step,
    registerError: undefined,
    progress: undefined,
    setProgress: undefined,
    sendOtp,
    verifyOtp,
    inputValue,
    dialCode,
    otpValue,
    isSignup: false,
  });

  // Set verifyType once on mount
  useEffect(() => {
    setVerifyType(loginMode === "mobile" ? "mobile" : "email");
  }, [loginMode, setVerifyType]);

  const keyboardBottomPadding = useKeyboardBottomPadding();
  useFixMobileKeyboard();

  // Handle input focus to prevent keyboard jump
  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Small delay to let keyboard animation start
    setTimeout(() => {
      e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  return (
    <React.Fragment>
      <div className="w-screen min-h-screen bg-white flex flex-col">
        {/* Onboarding Header with gradient, back/exit, and icon */}
        <MOnboardingHeader
          onBack={() => navigate(-1)}
          onExit={() => navigate('/')}
          showIcon={loginMode === 'mobile' ? 'phone' : 'email'}
        />

        <div
          className="flex-1 min-h-0 overflow-y-auto px-6 mt-8 pb-6"
        >
            {step === "input" && (
              <>
                {/* Title */}
                <h1 className="text-[24px] font-bold text-[#36116D] mb-2">
                  {loginMode === "mobile" ? t('auth.onboarding.signupForm.titleMobile') : t('auth.onboarding.signupForm.titleEmail')}
                </h1>
                <p className="text-[#474747] text-[16px] font-medium mb-8">
                  {t('auth.onboarding.signupForm.subtitle')}
                </p>

                {/* Input Section */}
                {loginMode === "mobile" ? (
                  <>
                    <div className="flex items-center gap-3 mb-6">
                      <div
                        className="flex items-center gap-2 px-4 py-3 border-2 border-[#6BA2EA] rounded-lg"
                      >
                        {(() => {
                          const codeObj = dialCodeOptions.find(
                            (opt) => opt.value === dialCode
                          );
                          return codeObj ? (
                            <img
                              src={`https://flagcdn.com/w40/${codeObj.countryCode.toLowerCase()}.png`}
                              alt={codeObj.countryCode}
                              className="w-6 h-4"
                            />
                          ) : null;
                        })()}
                        <span className="font-semibold text-gray-900">{dialCode}</span>
                      </div>
                      <MInputField
                        size="xl"
                        variant="Outlined"
                        state="Default"
                        inputMode="numeric"
                        type="number"
                        maxLength={10}
                        placeholder={t('auth.onboarding.signupForm.mobilePlaceholder')}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onFocus={handleInputFocus}
                        className="flex-1"
                      />
                    </div>
                    <div className="text-center mb-8">
                      <button
                        className="text-[#45168C] font-semibold text-sm"
                        onClick={() => navigate("/login/form", { state: { mode: 'email', isLogin: true } })}
                      >
                        {t('auth.onboarding.signupForm.useEmailInstead')}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <MInputField
                      size="xl"
                      variant="Outlined"
                      type="email"
                      placeholder={t('auth.onboarding.signupForm.emailPlaceholder')}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onFocus={handleInputFocus}
                      className="mb-6"
                      state="Default"
                    />

                    <div className="text-center mb-8">
                      <button
                        className="text-[#45168C] font-semibold text-sm"
                        onClick={() => navigate("/login/form", { state: { mode: 'mobile', isLogin: true } })}
                      >
                        {t('auth.onboarding.signupForm.useMobileInstead')}
                      </button>
                    </div>
                  </>
                )}

                {/* Button inside scrollable area */}
                <div className="mt-6 confirm-footer">
                  <MButton
                    variant="filled"
                    size="lg"
                    className="w-full"
                    onClick={handleCta}
                    disabled={
                      !inputValue || 
                      loading || 
                      (loginMode === "mobile" ? !isValidMobile(inputValue) : !isValidEmail(inputValue))
                    }
                  >
                    {t('auth.onboarding.signupForm.confirm')}
                  </MButton>
                </div>
              </>
            )}
            {step === "otp" && (
              <>
                {/* Title */}
                <h1 className="text-[24px] font-bold text-purple-900 mb-2">
                  {loginMode === "mobile" ? t('auth.onboarding.signupForm.titleMobile') : t('auth.onboarding.signupForm.titleEmail')}
                </h1>
                <p className="text-gray-600 otpText mb-8">
                  {t('auth.onboarding.signupForm.otpSent', { 
                    contact: loginMode === "mobile" ? `${dialCode} ${inputValue}` : inputValue 
                  })}
                </p>

                {/* OTP Input */}
                <MInputField
                  size="xl"
                  variant="Outlined"
                  state="Default"
                  inputMode="numeric"
                  type="number"
                  maxLength={6}
                  placeholder={t('auth.onboarding.signupForm.otpPlaceholder')}
                  value={otpValue}
                  autoFocus
                  onFocus={handleInputFocus}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "");
                    setOtpValue(val);
                  }}
                  className="mb-4"
                />

                {/* Resend code link */}
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-sm text-gray-600">{t('auth.onboarding.signupForm.didntGetCode')}</span>
                  <button
                    style={{color : "#B23F83"}}
                    className="text-sm font-semibold disabled:opacity-50"
                    disabled={resendTimer > 0 || loading}
                    onClick={() => {
                      if (resendTimer === 0 && !loading) handleResend();
                    }}
                  >
                    {t('auth.onboarding.signupForm.resendCode')}
                  </button>
                </div>

                {resendTimer > 0 && (
                  <p className="text-sm text-gray-500 mb-8">
                    {t('auth.onboarding.signupForm.resendIn', { seconds: resendTimer.toString().padStart(2, "0") })}
                  </p>
                )}

                {/* Button inside scrollable area */}
                <div className="mt-6 confirm-footer">
                  <MButton
                    variant="filled"
                    size="lg"
                    className="w-full"
                    onClick={handleCta}
                    disabled={otpValue.length !== 6 || loading}
                  >
                    {t('auth.onboarding.signupForm.confirm')}
                  </MButton>
                </div>
              </>
            )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default LoginForm;
